import { TAX_BRACKETS, TAX_FREE_ALLOWANCE, INSURANCE_RATE } from '../config';
import type { LimitsConfig } from './limits';

interface SalaryData {
  monthlySalary: number;
  additionalMonths: number;
  bonusMonths: number;
  monthlySubsidy: number;
  additionalTaxFree?: number;
  housingFundRate: number;
  province: string;
  city: string;
}

export function calculateSalary(
  {
    monthlySalary,
    additionalMonths,
    bonusMonths,
    monthlySubsidy,
    additionalTaxFree = 0,
    housingFundRate,
    province,
    city,
  }: SalaryData,
  limits: LimitsConfig
) {
  const annualBaseSalary = monthlySalary * (12 + additionalMonths);
  const yearEndBonus = monthlySalary * bonusMonths;
  const totalSubsidy = monthlySubsidy * 12;

  const insuranceDeduction = calculateInsurance(monthlySalary, province, limits);
  const housingFundDeduction = calculateHousingFund(monthlySalary + monthlySubsidy, housingFundRate, city, limits);
  const companyHousingFundContribution = calculateCompanyHousingFund(
    monthlySalary + monthlySubsidy,
    housingFundRate,
    city,
    limits
  );

  const totalTaxFree = TAX_FREE_ALLOWANCE + additionalTaxFree * 12;
  const taxableBaseSalary = annualBaseSalary + totalSubsidy - totalTaxFree - insuranceDeduction - housingFundDeduction;

  const taxBaseSalary = calculateTax(taxableBaseSalary, TAX_BRACKETS.annual);
  const taxBonus = calculateBonusTax(yearEndBonus);

  const totalSalaryAfterTax =
    annualBaseSalary +
    yearEndBonus +
    totalSubsidy -
    taxBaseSalary -
    taxBonus -
    insuranceDeduction -
    housingFundDeduction;

  return {
    totalSalaryAfterTax,
    totalSalaryWithHousingFund: totalSalaryAfterTax + housingFundDeduction + companyHousingFundContribution,
    totalSalaryWithoutHousingFund: totalSalaryAfterTax,
    annualBaseSalary,
    yearEndBonus,
    totalSubsidy,
    taxBaseSalary,
    taxBonus,
    insuranceDeduction,
    housingFundDeduction,
    companyHousingFundContribution,
  };
}

function calculateTax(amount: number, brackets: { limit: number; rate: number }[]): number {
  let tax = 0;
  let remaining = amount;

  for (const bracket of brackets) {
    if (remaining <= bracket.limit) {
      tax += remaining * bracket.rate;
      break;
    } else {
      tax += bracket.limit * bracket.rate;
      remaining -= bracket.limit;
    }
  }

  return tax;
}

function calculateBonusTax(bonus: number): number {
  const monthlyBonus = bonus / 12;
  let tax = 0;

  for (const bracket of TAX_BRACKETS.bonus) {
    if (monthlyBonus <= bracket.limit) {
      tax = bonus * bracket.rate - bracket.deduction;
      break;
    }
  }

  return tax;
}

function calculateInsurance(monthlySalary: number, province: string, limits: LimitsConfig): number {
  const limit = limits.insuranceBaseLimits[province];
  if (!limit) return 0;
  const baseSalary = Math.min(Math.max(monthlySalary, limit.min), limit.max);
  return baseSalary * INSURANCE_RATE * 12;
}

function calculateHousingFund(monthlySalary: number, rate: number, city: string, limits: LimitsConfig): number {
  const limit = limits.housingFundBaseLimits[city];
  if (!limit) return 0;
  const min = limit.min ?? 0;
  const baseSalary = Math.min(Math.max(monthlySalary, min), limit.max);
  return baseSalary * rate * 12;
}

function calculateCompanyHousingFund(monthlySalary: number, rate: number, city: string, limits: LimitsConfig): number {
  const limit = limits.housingFundBaseLimits[city];
  if (!limit) return 0;
  const min = limit.min ?? 0;
  const baseSalary = Math.min(Math.max(monthlySalary, min), limit.max);
  return baseSalary * rate * 12;
}